import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ArrowLeft,
  Music,
  Sparkles,
  Presentation,
  Store,
  Layers,
  Shapes,
  Maximize,
  Wrench,
  Weight,
  Palette,
  Puzzle,
  Video,
  Star,
  MapPin,
  Radio,
} from 'lucide-react';
import './LedVisionSub.css';

const HERO_IMG =
  'https://customer-assets-gfyr7b9c.emergentagent.net/job_security-display-hub/artifacts/e4pl6nbf_HMV-PRO_%E7%94%BB%E5%83%8F%E3%81%AE%E3%81%BF.png';
const IMG_HMV_PRO = HERO_IMG;
const IMG_HCF_PRO =
  'https://customer-assets-gfyr7b9c.emergentagent.net/job_security-display-hub/artifacts/g99586g0_HAW_%E7%94%BB%E5%83%8F%E3%81%AE%E3%81%BF.png';

const SeriesBlock = ({ s }) => {
  const Icon = s.Icon;
  return (
    <div className="lvs-series-detail" data-testid={`series-${s.name}`}>
      <div className="lvs-detail-top">
        {s.hasImage ? (
          <div className="lvs-detail-image"><img src={s.image} alt={s.name} /></div>
        ) : (
          <div className="lvs-detail-image-gradient" aria-hidden="true">
            <span className="lvs-detail-gradient-icon"><Icon size={44} strokeWidth={1.4} /></span>
          </div>
        )}
        <div className="lvs-detail-body">
          <span className="lvs-detail-name-tag">{s.name}</span>
          <h3 className="lvs-detail-title">{s.title}</h3>
          <p className="lvs-detail-tagline">{s.tagline}</p>
          <p className="lvs-detail-desc">{s.desc}</p>
        </div>
      </div>
      <div className="lvs-detail-features">
        <div className="lvs-feature-col">
          <div className="lvs-feature-head">
            <span><MapPin size={16} strokeWidth={1.7} /></span>
            用途 <small>/ Use</small>
          </div>
          <ul className="lvs-feature-list">
            {s.uses.map((u, k) => <li key={k}>{u}</li>)}
          </ul>
        </div>
        <div className="lvs-feature-col">
          <div className="lvs-feature-head">
            <span><Star size={16} strokeWidth={1.7} /></span>
            特長 <small>/ Features</small>
          </div>
          <ul className="lvs-feature-list">
            {s.features.map((f, k) => <li key={k}>{f}</li>)}
          </ul>
        </div>
      </div>
      <div className="lvs-spec-title">製品概要 / SPECIFICATIONS</div>
      <table className="lvs-spec-table">
        <tbody>
          {s.specs.map((row, k) => (
            <tr key={k}><th>{row[0]}</th><td>{row[1]}</td></tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const SpecialLedVision = () => {
  const useCases = [
    { Icon: Music, label: 'イベント・ステージ' },
    { Icon: Sparkles, label: '展示会・展示ブース' },
    { Icon: Video, label: 'バーチャルプロダクション' },
    { Icon: Presentation, label: 'ショーウィンドウ' },
    { Icon: Store, label: 'ガラス面演出' },
    { Icon: Shapes, label: '曲面演出' },
    { Icon: Layers, label: '空間演出' },
    { Icon: Maximize, label: '床面演出' },
  ];

  const series = [
    {
      name: 'HCF-PRO',
      title: 'HCF-PRO シリーズ',
      tagline: '曲面演出・イベント・レンタル向けLEDビジョン',
      desc: '曲面演出に対応したレンタル向けLEDビジョンです。高いリフレッシュレートと広い視野角により、カメラ撮影時でも滑らかで自然な映像表現を実現。軽量設計と多様な設置方法で、イベントやステージ演出をサポートします。',
      image: IMG_HCF_PRO,
      hasImage: true,
      uses: ['イベント・展示会', 'コンサート・ステージ演出', 'レンタル・ツアー向け', 'カメラ撮影を伴うライブ配信・収録'],
      features: ['曲面設置に対応（−40°〜+40°）', '高リフレッシュレート（〜7,680Hz）', '軽量・薄型で持ち運びや設置が容易', '広い視野角（H:160° / V:140°）'],
      specs: [
        ['主な用途', 'イベント・展示会 / コンサート / ステージ演出 / レンタル / ライブ配信'],
        ['ピクセルピッチ', '1.95 / 2.6 / 2.97 / 3.91 mm'],
        ['キャビネットサイズ', '500 × 500 × 94 mm'],
        ['特長', '曲面設置対応 / 高リフレッシュレート / 軽量設計 / 広視野角'],
      ],
    },
    {
      name: 'HCL',
      title: 'HCL シリーズ',
      tagline: '滑らかな曲面演出を実現するモデル',
      desc: '円柱や湾曲面など、曲面ディスプレイ演出に対応するシリーズです。屋内から屋外まで幅広いピッチをラインナップし、空間デザインやイベント演出に高い自由度をもたらします。',
      hasImage: false,
      Icon: Shapes,
      uses: ['曲面演出', 'イベント', '空間デザイン', '屋内外の曲面ディスプレイ'],
      features: ['屋内 PP 1.25〜3.91 / 屋外 PP 3.91mm', '滑らかな曲面演出', '空間デザインとの親和性', 'イベントから常設まで対応'],
      specs: [
        ['主な用途', '曲面演出 / イベント / 空間デザイン'],
        ['ピクセルピッチ', '屋内 1.25 / 1.56 / 1.95 / 2.6 / 3.91mm ・ 屋外 3.91mm'],
        ['特長', '曲面演出対応 / 幅広いピッチ選択 / 空間演出向け'],
      ],
    },
    {
      name: 'HAR-X',
      title: 'HAR-X フロアLEDシリーズ',
      tagline: '床荷重に配慮したフロアLED',
      desc: '床面演出・体験型コンテンツ・展示会など、来場者が上を歩いて楽しめる床面LEDビジョンです。耐荷重・耐衝撃仕様で、視覚と体験を融合したイベント演出を支えます。',
      hasImage: false,
      Icon: Maximize,
      uses: ['床面演出', '体験型コンテンツ', '展示会', 'イベント床面'],
      features: ['耐荷重・耐衝撃設計', '床面設置対応', '体験型イベントに最適', '安全性に配慮した表面加工'],
      specs: [
        ['主な用途', '床面演出 / 体験型コンテンツ / 展示会'],
        ['ピクセルピッチ', '3.91 mm'],
        ['特長', '耐荷重仕様 / 床面設置対応 / 体験型演出向け'],
      ],
    },
    {
      name: 'HVA / HVA-mini',
      title: 'HVA / HVA-mini シリーズ',
      tagline: '透過性を活かしたLEDビジョン',
      desc: '透過性に優れたデザインにより、自然光や景観を活かした演出を実現するLEDビジョンです。軽量設計で設置性が高く、屋外イベントから商業施設のガラス面装飾まで、幅広いシーンに対応します。',
      hasImage: false,
      Icon: Layers,
      uses: ['屋外イベント・コンサート', '商業施設・建物のガラス面装飾', 'ショーウィンドウ・ファサード演出'],
      features: ['高透過率で自然光や景観を活かした演出が可能', '軽量・薄型設計で設置やメンテナンスが容易', '角度調整（直角/90°）に対応', 'IP65規格の防塵・防水性能（屋外）'],
      specs: [
        ['主な用途', '屋外イベント / 商業施設ガラス面 / ショーウィンドウ / ファサード演出'],
        ['ピクセルピッチ', 'HVA: 3.91〜7.81 / 8.93 mm ・ HVA-mini: 3.91〜7.81 / 8.93 mm'],
        ['キャビネットサイズ', 'HVA: 1000×500×88.5mm / 500×500×88.5mm ・ HVA-mini: 500×500×88.5mm'],
        ['特長', '透過率 15〜50%（モデルにより異なる）/ 軽量・薄型 / 屋外環境下でも安定運用'],
      ],
    },
    {
      name: 'HMV-PRO',
      title: 'HMV-PRO シリーズ',
      tagline: 'バーチャルプロダクション・XR向けLEDビジョン',
      desc: '高精細・高リフレッシュレートに対応した、バーチャルプロダクション／XR向けLEDビジョンです。放送・映像制作の現場で求められる色精度と安定性を備え、臨場感のある映像表現をサポートします。',
      image: IMG_HMV_PRO,
      hasImage: true,
      uses: ['バーチャルプロダクション', 'XRスタジオ', '放送・映像制作', 'ライブ配信・イベント演出'],
      features: ['高リフレッシュレート（最大7,680Hz）', '高精細・高輝度・高コントラスト', 'ワンタッチロック機構で簡単施工', '前面メンテナンス対応', 'カーブ設置・吊り設置対応'],
      specs: [
        ['主な用途', 'バーチャルプロダクション / XRスタジオ / 放送・映像制作'],
        ['ピクセルピッチ', '1.01 / 1.21 / 1.51 / 2.02 mm（4モデル）'],
        ['キャビネットサイズ', '500 × 500 × 72 mm'],
        ['特長', '高リフレッシュレート / 高精細・高輝度 / ワンタッチロック機構 / カーブ・吊り設置対応'],
      ],
    },
  ];

  const points = [
    { Icon: Puzzle, title: '設置形状・スペース', body: '平面・曲面・床面・ガラス面など、設置場所の条件に合わせてタイプを選定します。' },
    { Icon: Palette, title: '表示コンテンツ', body: '映像・グラフィック・体験型演出など、コンテンツの性質に合わせた仕様を検討します。' },
    { Icon: Weight, title: '短期・常設の別', body: 'レンタル利用と常設利用では、筐体構造・取付方法・保守条件が異なります。' },
    { Icon: Wrench, title: '耐荷重・撮影対応', body: '床面設置や撮影を伴う現場では、耐荷重・リフレッシュレート・色精度も併せてご提案します。' },
  ];

  return (
    <div className="lvs-page" data-testid="led-vision-special-page">
      <section className="lvs-hero" data-testid="special-hero">
        <div className="lvs-hero-bg" aria-hidden="true"><img src={HERO_IMG} alt="" /></div>
        <div className="lvs-hero-overlay" aria-hidden="true" />
        <div className="lvs-hero-inner">
          <div className="lvs-hero-content">
            <Link to="/products/led-vision" className="lvs-back" data-testid="back-to-led-vision">
              <ArrowLeft size={14} /> LEDビジョン一覧へ戻る
            </Link>
            <span className="lvs-hero-eyebrow">SPECIAL USE LED VISION</span>
            <h1 className="lvs-hero-title">特殊用途LEDビジョン</h1>
            <p className="lvs-hero-sub">空間や演出に、自由な発想を。</p>
            <p className="lvs-hero-desc">
              曲面・床面・透過・バーチャルプロダクションなど、標準的な壁面設置では対応が難しい演出・撮影・体験型シーンに向けたLEDビジョンです。用途に合わせて最適なシリーズをご提案します。
            </p>
            <div className="lvs-hero-actions">
              <Link to="/contact" className="lvs-btn lvs-btn-primary">お問い合わせ <ArrowRight size={18} /></Link>
              <a href="#series" className="lvs-btn lvs-btn-outline">取扱シリーズを見る <ArrowRight size={18} /></a>
            </div>
          </div>
        </div>
      </section>

      <section className="lvs-section">
        <div className="lvs-container">
          <div className="lvs-two-col">
            <div className="lvs-intro-text">
              <span className="lvs-eyebrow">ABOUT</span>
              <h2 className="lvs-h2">特殊用途LEDビジョンとは</h2>
              <p>特殊用途LEDビジョンは、標準的な四角い壁面設置にとどまらず、イベント・演出・撮影・体験型など多様な用途に対応するタイプの総称です。曲面演出、床面演出、ガラス面の透過演出、バーチャルプロダクション向けの高精細モデルまで、目的に合わせて選定します。</p>
            </div>
            <div className="lvs-intro-visual" aria-hidden="true"><img src={HERO_IMG} alt="" /></div>
          </div>
        </div>
      </section>

      <section className="lvs-section lvs-section-gray">
        <div className="lvs-container">
          <div className="lvs-head">
            <span className="lvs-eyebrow">USE CASES</span>
            <h2 className="lvs-h2">主な用途</h2>
            <p className="lvs-lead">イベント・演出・展示・撮影・体験型など、幅広いシーンでご活用いただけます。</p>
          </div>
          <div className="lvs-use-grid">
            {useCases.map((u, i) => { const Icon = u.Icon;
              return (
                <div key={i} className="lvs-use-item" data-testid={`use-${i}`}>
                  <span className="lvs-use-icon"><Icon size={18} strokeWidth={1.7} /></span>
                  <span className="lvs-use-label">{u.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="lvs-section" id="series">
        <div className="lvs-container">
          <div className="lvs-head">
            <span className="lvs-eyebrow">PRODUCT SERIES</span>
            <h2 className="lvs-h2">取扱シリーズ</h2>
            <p className="lvs-lead">用途に合わせて、5つのシリーズをご用意しています。</p>
          </div>
          {series.map((s, i) => <SeriesBlock key={i} s={s} />)}
        </div>
      </section>

      <section className="lvs-section lvs-section-gray">
        <div className="lvs-container">
          <div className="lvs-head">
            <span className="lvs-eyebrow">SELECTION</span>
            <h2 className="lvs-h2">選定ポイント</h2>
            <p className="lvs-lead">演出目的や設置場所を確認したうえで、最適なタイプをご提案します。</p>
          </div>
          <div className="lvs-points">
            {points.map((p, i) => { const Icon = p.Icon;
              return (
                <div key={i} className="lvs-point-card" data-testid={`point-${i}`}>
                  <span className="lvs-point-icon"><Icon size={20} strokeWidth={1.7} /></span>
                  <h3 className="lvs-point-title">{p.title}</h3>
                  <p className="lvs-point-body">{p.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="lvs-cta" data-testid="cta">
        <div className="lvs-cta-inner">
          <div className="lvs-cta-text">
            <h2>特殊用途LEDビジョンのご相談は、株式会社エイチエスへ。</h2>
            <p>設置場所・用途・演出内容に合わせて、最適なタイプをご提案いたします。</p>
          </div>
          <div className="lvs-cta-actions">
            <Link to="/contact" className="lvs-btn lvs-btn-primary">お問い合わせ <ArrowRight size={18} /></Link>
            <Link to="/products/led-vision" className="lvs-btn lvs-btn-outline"><ArrowLeft size={16} /> LEDビジョン一覧</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SpecialLedVision;
